/**
 * Browser-session renewal — the ledger behind `/internal/browser-sessions/…/renewals`
 * (plan 2026-09-14-0916, SCH-1813, U2).
 *
 * The backend decides who may sign in to the shared Apple ID, when, and how
 * often; the browser runner reports what happened. Everything a runner sends
 * about an outcome is a CODE from the closed lists below (KTD16) — never free
 * text — so nothing Apple said, and nothing the child printed, can reach the
 * ledger, an operations event or Slack (R11).
 *
 * Ownership: `apps/backend/src/services/browser-sessions/renewal/outcome-codes.ts`
 * owns the POLICY around these codes (which halt, what message each becomes).
 * This file is the wire contract the runner and the console type against, and
 * it holds the lists so they cannot drift between the three apps. U5 adds the
 * console request/response schemas here.
 */

import { z } from 'zod'

// =============================================================================
// Vocabularies
// =============================================================================

/** Who started the renewal. A queued job carries no authority of its own (KTD15). */
export const RENEWAL_TRIGGERS = ['DISPATCH', 'SCHEDULED', 'CONSOLE'] as const
export const RenewalTriggerSchema = z.enum(RENEWAL_TRIGGERS)
export type RenewalTrigger = z.infer<typeof RenewalTriggerSchema>

/**
 * How the sign-in runs. `AUTO` is what a trigger asks for and what a REQUESTED
 * row carries; the claim resolves it (KTD10) and every row past CLAIMED holds
 * `REFRESH` (password + stored trust cookie) or `ROTATE_TRUST` (password with
 * no trust cookie, forcing one SMS and minting a fresh trust).
 */
export const RENEWAL_MODES = ['AUTO', 'REFRESH', 'ROTATE_TRUST'] as const
export const RenewalModeSchema = z.enum(RENEWAL_MODES)
export type RenewalMode = z.infer<typeof RenewalModeSchema>

/** The mode a caller may request, in the console's spelling. */
export const RenewalModeRequestSchema = z.enum(['auto', 'refresh', 'rotate-trust'])
export type RenewalModeRequest = z.infer<typeof RenewalModeRequestSchema>

/** The states in the plan's renewal state diagram. */
export const RENEWAL_STATUSES = [
  'REQUESTED',
  'CLAIMED',
  'SIGNING_IN',
  'AWAITING_CODE',
  /**
   * `complete` has claimed the row and is saving the jar. Only one `complete`
   * can move a row here, so a duplicate completion (a retried request racing
   * the first) is refused before it can touch the store or the outcome.
   */
  'COMPLETING',
  'SUCCEEDED',
  'FAILED',
  'REFUSED',
  'ABANDONED',
  'HALTED'
] as const
export const RenewalStatusSchema = z.enum(RENEWAL_STATUSES)
export type RenewalStatus = z.infer<typeof RenewalStatusSchema>

/** States in which a runner still holds the lock. Progress and fail reports are accepted from the first three only. */
export const RENEWAL_IN_FLIGHT_STATUSES = ['CLAIMED', 'SIGNING_IN', 'AWAITING_CODE', 'COMPLETING'] as const

/** States nothing will move a row out of. */
export const RENEWAL_TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'REFUSED', 'ABANDONED', 'HALTED'] as const

export const RENEWAL_CODE_SOURCES = ['TWILIO', 'OPERATOR'] as const
export const RenewalCodeSourceSchema = z.enum(RENEWAL_CODE_SOURCES)
export type RenewalCodeSource = z.infer<typeof RenewalCodeSourceSchema>

// =============================================================================
// Outcome codes (KTD16) — the closed list
// =============================================================================

/** Raised by the Ruby sign-in child. The first six halt (R9); `code-rejected` halts on the second within 24 h. */
export const RUBY_CHILD_OUTCOME_CODES = [
  'password-rejected',
  'account-locked',
  'too-many-codes',
  'sign-in-forbidden',
  'terms-acknowledgement-needed',
  'no-matching-trusted-number',
  'code-rejected',
  'code-timeout',
  'sms-refused',
  'trust-missing',
  'unexpected-prompt',
  'portal-error',
  'usage'
] as const

/** Raised by the runner around the child. None halts. */
export const RUNNER_OUTCOME_CODES = [
  'config-missing',
  'secret-unreadable',
  'spawn-failed',
  'child-deadline',
  'malformed-output',
  'code-ambiguous'
] as const

/** Written by the backend itself; a runner can never send one. None halts. */
export const BACKEND_OUTCOME_CODES = ['store-revoked', 'store-conflict', 'scheduled-too-soon'] as const

/** The codes `POST …/renewals/:id/fail` accepts. */
export const FAIL_OUTCOME_CODES = [...RUBY_CHILD_OUTCOME_CODES, ...RUNNER_OUTCOME_CODES] as const
export const RenewalFailOutcomeCodeSchema = z.enum(FAIL_OUTCOME_CODES)
export type RenewalFailOutcomeCode = z.infer<typeof RenewalFailOutcomeCodeSchema>

/** Every outcome a finished sign-in can carry. `succeeded` is the only success. */
export const RENEWAL_OUTCOME_CODES = ['succeeded', ...FAIL_OUTCOME_CODES, ...BACKEND_OUTCOME_CODES] as const
export const RenewalOutcomeCodeSchema = z.enum(RENEWAL_OUTCOME_CODES)
export type RenewalOutcomeCode = z.infer<typeof RenewalOutcomeCodeSchema>

/**
 * Why a claim was REFUSED. A refused row counts as neither a sign-in nor a
 * failure (KTD6): nothing was submitted to Apple.
 */
export const RENEWAL_REFUSAL_REASONS = [
  'disabled',
  'flag-unreadable',
  'cooldown',
  'capped-sign-ins',
  'capped-sms',
  'scheduled-too-soon',
  'source-key-reused',
  'dispatch-not-in-flight',
  'request-not-claimable',
  'lock-held',
  'store-revoked',
  'store-unreadable',
  'already-renewed',
  /** A renewal id already on the ledger was presented with a different slug, trigger or source key. */
  'renewal-id-mismatch'
] as const
export const RenewalRefusalReasonSchema = z.enum(RENEWAL_REFUSAL_REASONS)
export type RenewalRefusalReason = z.infer<typeof RenewalRefusalReasonSchema>

/** Endings only the ledger's own sweep or the console's enqueue path can write. */
export const RENEWAL_LEDGER_ENDINGS = ['lease-expired', 'request-unclaimed', 'enqueue-failed'] as const
export const RenewalLedgerEndingSchema = z.enum(RENEWAL_LEDGER_ENDINGS)
export type RenewalLedgerEnding = z.infer<typeof RenewalLedgerEndingSchema>

/** Everything `BrowserSessionRenewal.outcomeCode` may hold. */
export const RenewalRecordedCodeSchema = z.enum([
  ...RENEWAL_OUTCOME_CODES,
  ...RENEWAL_REFUSAL_REASONS,
  ...RENEWAL_LEDGER_ENDINGS
])
export type RenewalRecordedCode = z.infer<typeof RenewalRecordedCodeSchema>

// =============================================================================
// The ledger row, as every reader sees it
// =============================================================================

/**
 * Renewal ids are runner-generated for dispatch and scheduled claims (so a
 * retried claim is idempotent) and backend-generated for console requests.
 * Bounded to what `renewal_<id>` leaves inside the 64-character operations-event
 * correlation column.
 */
export const RenewalIdSchema = z
  .string()
  .min(8)
  .max(48)
  .regex(/^[A-Za-z0-9_-]+$/, 'renewal id must be URL-safe (letters, digits, _ and -)')

/**
 * One `BrowserSessionRenewal` row. Never carries a cookie value, a password or
 * a code — there is no field for one.
 */
export const BrowserSessionRenewalSchema = z.object({
  id: RenewalIdSchema,
  slug: z.string(),
  sourceKey: z.string(),
  trigger: RenewalTriggerSchema,
  mode: RenewalModeSchema,
  requestedByUserId: z.string().nullable(),
  dispatchId: z.string().nullable(),
  status: RenewalStatusSchema,
  /** `true` while the row holds the slug's lock (in flight or halted). */
  lockHeld: z.boolean(),
  leaseExpiresAt: z.string().describe('ISO timestamp'),
  passwordSubmittedAt: z.string().nullable(),
  smsRequestedAt: z.string().nullable(),
  codeSource: RenewalCodeSourceSchema.nullable(),
  codeSubmittedByUserId: z.string().nullable(),
  codeChannel: z.string().nullable(),
  codeDeadlineAt: z.string().nullable(),
  outcomeCode: RenewalRecordedCodeSchema.nullable(),
  outcomeMessage: z.string().nullable(),
  twoFactorUsed: z.boolean(),
  trustExpiresAt: z.string().nullable(),
  teamsCount: z.number().int().nullable(),
  observedSessionVersion: z.number().int().nullable(),
  claimedSessionVersion: z.number().int().nullable(),
  savedSessionVersion: z.number().int().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  haltResolvedAt: z.string().nullable(),
  haltResolvedByUserId: z.string().nullable()
})
export type BrowserSessionRenewal = z.infer<typeof BrowserSessionRenewalSchema>

// =============================================================================
// POST /internal/browser-sessions/:slug/renewals — claim
// =============================================================================

/** The source of a claim, discriminated on the trigger it must prove (KTD15). */
export const ClaimRenewalRequestSchema = z.discriminatedUnion('trigger', [
  z.object({
    trigger: z.literal('DISPATCH'),
    renewalId: RenewalIdSchema,
    /** The `ProvisioningDispatch` public id; must be in flight on this slug. */
    dispatchId: z.string().min(1).max(64),
    /** `open` or `mid-run` — one renewal per phase, so the key differs per phase. */
    phase: z.enum(['open', 'mid-run']),
    mode: RenewalModeRequestSchema.default('auto'),
    /** The store version the dispatch opened with; `null` when it found no session. */
    observedSessionVersion: z.number().int().nonnegative().nullable()
  }),
  z.object({
    trigger: z.literal('SCHEDULED'),
    renewalId: RenewalIdSchema,
    /** The scheduled-task execution id; the source key. */
    executionId: z.string().min(1).max(128),
    /** A scheduled claim always runs `auto` (KTD15). */
    observedSessionVersion: z.number().int().nonnegative().nullable()
  }),
  z.object({
    trigger: z.literal('CONSOLE'),
    /** The REQUESTED row's id, which the job received as `requestId`. */
    renewalId: RenewalIdSchema,
    observedSessionVersion: z.number().int().nonnegative().nullable()
  })
])
export type ClaimRenewalRequest = z.infer<typeof ClaimRenewalRequestSchema>

/** Storage-state shape, validated to its outer shape only (see the store). */
export const RenewalSessionPayloadSchema = z.object({
  cookies: z.array(z.unknown()),
  origins: z.array(z.unknown())
})

/** What a WON claim hands the runner. The jar appears here and nowhere else in this contract. */
export const ClaimedRenewalSchema = z.object({
  kind: z.literal('claimed'),
  renewalId: RenewalIdSchema,
  slug: z.string(),
  mode: z.enum(['REFRESH', 'ROTATE_TRUST']),
  leaseExpiresAt: z.string(),
  /** The store version the claim read; `complete`'s compare-and-set guards on it. `null` when nothing is saved. */
  claimedSessionVersion: z.number().int().nullable(),
  /** The stored jar, decrypted, even past the store's TTL. `null` when nothing is saved. */
  session: RenewalSessionPayloadSchema.nullable(),
  /** Trust expiry the mode was resolved from, when known. */
  trustExpiresAt: z.string().nullable(),
  /** Flag knobs the runner honours during the sign-in. */
  twilioAutoRead: z.boolean(),
  appleSenders: z.array(z.string())
})
export type ClaimedRenewal = z.infer<typeof ClaimedRenewalSchema>

/** Another renewal holds the slug. The caller follows the record (KTD11). */
export const RenewalHolderSchema = z.object({
  renewalId: RenewalIdSchema,
  status: RenewalStatusSchema,
  leaseExpiresAt: z.string(),
  outcomeCode: RenewalRecordedCodeSchema.nullable()
})

export const ClaimRenewalResponseSchema = z.discriminatedUnion('kind', [
  ClaimedRenewalSchema,
  z.object({ kind: z.literal('in-flight'), holder: RenewalHolderSchema }),
  z.object({ kind: z.literal('halted'), holder: RenewalHolderSchema }),
  z.object({
    kind: z.literal('already-renewed'),
    renewalId: RenewalIdSchema,
    /** The store's current version — reopen the saved session with it. */
    savedSessionVersion: z.number().int()
  }),
  z.object({
    kind: z.literal('refused'),
    /** `null` when no row could be written (a reused source key). */
    renewalId: RenewalIdSchema.nullable(),
    reason: RenewalRefusalReasonSchema,
    /** For `cooldown`: the failed renewal's outcome and when the cooldown ends. */
    cooldownUntil: z.string().nullable().optional(),
    blockedByOutcomeCode: RenewalRecordedCodeSchema.nullable().optional()
  })
])
export type ClaimRenewalResponse = z.infer<typeof ClaimRenewalResponseSchema>

// =============================================================================
// Runner reports on a held renewal
// =============================================================================

/**
 * `POST …/renewals/:id/progress`. Each step is a guarded state move; a report
 * against a row the caller no longer holds is a 409.
 */
export const RenewalProgressRequestSchema = z.discriminatedUnion('step', [
  /** Immediately before the child is spawned: stamps `passwordSubmittedAt`, CLAIMED → SIGNING_IN. */
  z.object({ step: z.literal('password-submitting') }),
  /** Apple asked for a code: SIGNING_IN → AWAITING_CODE, with where it went and the runner's deadline. */
  z.object({
    step: z.literal('awaiting-code'),
    codeChannel: z.enum(['sms', 'device', 'voice', 'unknown']),
    codeDeadlineAt: z.string().datetime()
  }),
  /** A code went to Apple: AWAITING_CODE → SIGNING_IN, recording which channel supplied it. */
  z.object({ step: z.literal('code-submitted'), codeSource: RenewalCodeSourceSchema })
])
export type RenewalProgressRequest = z.infer<typeof RenewalProgressRequestSchema>

/** `POST …/renewals/:id/sms-authorize` — answers before the child may request an SMS. */
export const SmsAuthorizeResponseSchema = z.discriminatedUnion('allowed', [
  z.object({ allowed: z.literal(true), smsRequestedAt: z.string() }),
  z.object({
    allowed: z.literal(false),
    reason: z.enum(['capped-sms', 'sms-already-requested', 'flag-unreadable', 'disabled'])
  })
])
export type SmsAuthorizeResponse = z.infer<typeof SmsAuthorizeResponseSchema>

/** `POST …/renewals/:id/complete` — the child's stdout document, minus anything free-text. */
export const CompleteRenewalRequestSchema = z.object({
  session: RenewalSessionPayloadSchema,
  twoFactorUsed: z.boolean(),
  /** Whether an SMS went out this renewal (Apple's own or ours); counted after the fact when unstamped. */
  smsRequested: z.boolean(),
  trustExpiresAt: z.string().datetime().nullable(),
  teamsCount: z.number().int().nonnegative().nullable()
})
export type CompleteRenewalRequest = z.infer<typeof CompleteRenewalRequestSchema>

export const CompleteRenewalResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('saved'), renewalId: RenewalIdSchema, savedSessionVersion: z.number().int() }),
  /** The save did not land; the renewal is FAILED with the backend code that says why (R10). */
  z.object({
    kind: z.literal('failed'),
    renewalId: RenewalIdSchema,
    outcomeCode: z.enum(['store-revoked', 'store-conflict'])
  })
])
export type CompleteRenewalResponse = z.infer<typeof CompleteRenewalResponseSchema>

/**
 * `POST …/renewals/:id/fail` — a code, plus whether an SMS went out before the
 * failure. Apple can send a text on its own the moment the password lands, and
 * a renewal that then times out or has its code rejected still cost one SMS:
 * the cap counts it here, the same way `complete` counts it on success.
 */
export const FailRenewalRequestSchema = z
  .object({ outcomeCode: RenewalFailOutcomeCodeSchema, smsRequested: z.boolean().optional() })
  .strict()
export type FailRenewalRequest = z.infer<typeof FailRenewalRequestSchema>

export const FailRenewalResponseSchema = z.object({
  renewalId: RenewalIdSchema,
  status: z.enum(['FAILED', 'HALTED']),
  outcomeCode: RenewalFailOutcomeCodeSchema
})
export type FailRenewalResponse = z.infer<typeof FailRenewalResponseSchema>

/**
 * `POST …/renewals/:id/events` — the two events the runner records mid-flight.
 * Numbers and enums only: a free-text field here would be a leak path (R11).
 */
export const RenewalEventRequestSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('renewal_awaiting_code'),
    reason: z.enum(['twilio-timeout', 'twilio-auto-read-off', 'code-ambiguous']),
    candidateCount: z.number().int().nonnegative().optional()
  }),
  z.object({
    type: z.literal('code_incident'),
    unmatchedCount: z.number().int().positive(),
    candidateCount: z.number().int().nonnegative()
  })
])
export type RenewalEventRequest = z.infer<typeof RenewalEventRequestSchema>

// =============================================================================
// Operator-typed code
// =============================================================================

/** A six-digit Apple code, and nothing else. */
export const OperatorCodeSchema = z.string().regex(/^\d{6}$/, 'a six-digit code')

/** The console's write (U5 routes it). */
export const SubmitOperatorCodeRequestSchema = z.object({ code: OperatorCodeSchema }).strict()
export type SubmitOperatorCodeRequest = z.infer<typeof SubmitOperatorCodeRequestSchema>

/**
 * `GET …/renewals/:id/operator-code` — read-and-delete. `unavailable` is
 * distinct from `none` on purpose: a runner that cannot tell "nobody typed
 * anything" from "the code store is down" would wait out its deadline on a
 * code that was typed.
 */
export const OperatorCodeReadResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('code'), code: OperatorCodeSchema, submittedByUserId: z.string().nullable() }),
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('unavailable') })
])
export type OperatorCodeReadResponse = z.infer<typeof OperatorCodeReadResponseSchema>

// =============================================================================
// Halt state (KTD17) and the record read
// =============================================================================

export const RenewalHaltStateResponseSchema = z.object({
  slug: z.string(),
  halted: z.boolean(),
  /** The HALTED row, when one holds the lock. */
  renewal: BrowserSessionRenewalSchema.nullable()
})
export type RenewalHaltStateResponse = z.infer<typeof RenewalHaltStateResponseSchema>

export const RenewalReadResponseSchema = BrowserSessionRenewalSchema

// =============================================================================
// The super-admin console (U5)
// =============================================================================

/**
 * The one session slug renewal is built for. The `BrowserSessionRenewal` ledger
 * is keyed by slug so a second self-renewing session could join later, but only
 * this one has a Ruby sign-in behind it today — and, per KTD17, its halt also
 * closes the Playwright `apple-developer` login, because both share one Apple ID.
 */
export const RENEWAL_SESSION_SLUG = 'apple-spaceship'

/** `POST /v1/mobile-apps/browser-sessions/:slug/renewals` — start one renewal from the console. */
export const StartRenewalRequestSchema = z.object({ mode: RenewalModeRequestSchema.default('auto') }).strict()
export type StartRenewalRequest = z.infer<typeof StartRenewalRequestSchema>

/**
 * The REQUESTED row plus the execution row its job will report against. The
 * console polls the renewal with the health summary; `executionId` is there so
 * an operator can find the ECS run when the job never reports at all.
 */
export const StartRenewalResponseSchema = z.object({
  renewal: BrowserSessionRenewalSchema,
  executionId: z.string()
})
export type StartRenewalResponse = z.infer<typeof StartRenewalResponseSchema>

/** `POST /v1/mobile-apps/browser-sessions/renewals/:id/operator-code`. */
export const SubmitOperatorCodeResponseSchema = z.object({
  renewalId: RenewalIdSchema,
  expiresInSeconds: z.number().int().positive()
})
export type SubmitOperatorCodeResponse = z.infer<typeof SubmitOperatorCodeResponseSchema>

/** `POST /v1/mobile-apps/browser-sessions/renewals/:id/resume`. */
export const ResumeRenewalResponseSchema = z.object({
  renewalId: RenewalIdSchema,
  haltResolvedAt: z.string()
})
export type ResumeRenewalResponse = z.infer<typeof ResumeRenewalResponseSchema>

/**
 * How the stored session reads right now, in the same vocabulary pre-flight
 * refuses in (`launch-preconditions.ts`), so the card and a refused dispatch cannot
 * describe the same session differently.
 */
export const RenewalSessionStateSchema = z.enum(['NOT_SAVED', 'REVOKED', 'EXPIRED', 'EXPIRING_SOON', 'FRESH'])
export type RenewalSessionState = z.infer<typeof RenewalSessionStateSchema>

/** Where the caps stand for the current UTC day (KTD6). Every number is computed by the backend. */
export const RenewalCapUsageSchema = z.object({
  signInsUsed: z.number().int().nonnegative(),
  /** `null` when the flag is absent or unreadable — there is no cap to compare against. */
  maxSignInsPerDay: z.number().int().nullable(),
  smsUsed: z.number().int().nonnegative(),
  maxSmsPerDay: z.number().int().nullable(),
  /** The next UTC midnight; both counters are windowed on the UTC calendar day. */
  capResetsAt: z.string()
})
export type RenewalCapUsage = z.infer<typeof RenewalCapUsageSchema>

/** The flag as the console shows it. `enabled` is false whenever the row is absent or unreadable. */
export const RenewalFlagSummarySchema = z.object({
  state: z.enum(['configured', 'absent', 'unreadable']),
  enabled: z.boolean(),
  twilioAutoRead: z.boolean().nullable(),
  cooldownMinutes: z.number().int().nullable()
})
export type RenewalFlagSummary = z.infer<typeof RenewalFlagSummarySchema>

/**
 * `GET /v1/mobile-apps/browser-sessions/:slug/renewals` — everything the card
 * needs in one read (U5 approach step 4).
 *
 * Carries no cookie value, no password and no code: it is composed from the
 * ledger, the flag and the store's FRESHNESS read, which never decrypts a jar.
 * The one exception is `trustExpiresAt`, which falls back to the stored jar's
 * `DES…` cookie when no renewal has ever recorded one — a date, not a value.
 */
export const RenewalHealthSummarySchema = z.object({
  slug: z.string(),
  observedAt: z.string(),
  session: z.object({
    state: RenewalSessionStateSchema,
    saved: z.boolean(),
    isActive: z.boolean(),
    expiresAt: z.string().nullable()
  }),
  flag: RenewalFlagSummarySchema,
  caps: RenewalCapUsageSchema,
  /** The renewal holding the lock right now, if any, with its step and code box state. */
  inFlight: BrowserSessionRenewalSchema.nullable(),
  /** How long the in-flight renewal has been running, so the card can show elapsed time. */
  inFlightElapsedMs: z.number().int().nonnegative().nullable(),
  /** The HALTED row still holding the lock (KTD17). `resume` is the only way out. */
  halted: BrowserSessionRenewalSchema.nullable(),
  /** Most recent rows first, newest `RECENT_RENEWAL_LIMIT`. */
  recent: z.array(BrowserSessionRenewalSchema),
  /** Set while a FAILED renewal's cooldown is still running; every trigger is blocked until then. */
  cooldownUntil: z.string().nullable(),
  cooldownOutcomeCode: RenewalRecordedCodeSchema.nullable(),
  /** From the latest SUCCEEDED row, or the stored jar when none exists (KTD10). */
  trustExpiresAt: z.string().nullable(),
  /** `true` when no renewal has ever succeeded here — the card reads "never renewed". */
  neverRenewed: z.boolean()
})
export type RenewalHealthSummary = z.infer<typeof RenewalHealthSummarySchema>

/** How many finished renewals the summary carries. */
export const RECENT_RENEWAL_LIMIT = 10
